/**
 * High-performance map using numeric keys instead of strings
 *
 * Uses Cantor pairing function to map (x, y) -> unique number
 * Significantly faster than string concatenation
 */

export class NumericKeyMap<V> {
  private map = new Map<number, V>();

  /**
   * Cantor pairing function with support for negative integers
   * Maps (x, y) -> unique non-negative integer
   */
  private hash(x: number, y: number): number {
    // Map integers to non-negative integers: 0 -> 0, -1 -> 1, 1 -> 2, -2 -> 3, 2 -> 4...
    const mapToPositive = (n: number): number => {
      const i = Math.floor(n);
      return i >= 0 ? 2 * i : -2 * i - 1;
    };

    const px = mapToPositive(x);
    const py = mapToPositive(y);
    return ((px + py) * (px + py + 1)) / 2 + py;
  }

  set(x: number, y: number, value: V): void {
    this.map.set(this.hash(x, y), value);
  }

  get(x: number, y: number): V | undefined {
    return this.map.get(this.hash(x, y));
  }

  has(x: number, y: number): boolean {
    return this.map.has(this.hash(x, y));
  }

  delete(x: number, y: number): boolean {
    return this.map.delete(this.hash(x, y));
  }

  clear(): void {
    this.map.clear();
  }

  get size(): number {
    return this.map.size;
  }

  values(): IterableIterator<V> {
    return this.map.values();
  }

  entries(): IterableIterator<[number, V]> {
    return this.map.entries();
  }

  forEach(callback: (value: V, key: number) => void): void {
    this.map.forEach(callback);
  }
}
